import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  Form,
  Input,
  Skeleton,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ChevronLeft, Pencil } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  type TeamList,
  organizationsAccessListOptions,
  organizationsTeamsCreateMutation,
  organizationsTeamsListOptions,
  organizationsTeamsListQueryKey,
  organizationsTeamsPartialUpdateMutation,
  organizationsTeamsRetrieveOptions,
  organizationsTeamsRetrieveQueryKey
} from 'src/api/main'
import {
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { useDocumentTitle } from 'src/hooks'
import { paths } from 'src/router/paths'
import { useShowApiErrors } from 'src/utils'

import { orgEntityListCardHeaderStyles } from '../components/workspaceOrgChart/OrgEntityListCard'
import {
  EntityFormFields,
  type EntityFormValues,
  type TeamWithHead
} from '../components/workspaceOrgChart/WorkspaceOrgEntityPanels'

import { useWorkspaceRoute } from './WorkspacePageShell'

export const WorkspaceOrgDepartmentDetailPage = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { id: workspaceRouteId = '', departmentId = '' } = useParams<{
    id: string
    departmentId: string
  }>()
  const navigate = useNavigate()
  const { workspaceId, idValid } = useWorkspaceRoute()
  const numericId = Number(departmentId)

  const [editOpen, setEditOpen] = useState(false)
  const [editForm] = Form.useForm<EntityFormValues>()
  const [addTeamOpen, setAddTeamOpen] = useState(false)
  const [teamForm] = Form.useForm<{ name: string }>()

  const {
    data: rawDepartment,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsTeamsRetrieveOptions({ path: { id: numericId } }),
    enabled: !Number.isNaN(numericId) && numericId > 0
  })

  const department = rawDepartment as TeamWithHead | undefined

  useDocumentTitle(department?.name)

  // Child teams (type=section, parent=this department)
  const { data: childTeamsPage } = useQuery({
    ...organizationsTeamsListOptions({
      query: {
        organization: workspaceId,
        type: ['section'],
        per_page: 200
      }
    }),
    enabled: idValid && !Number.isNaN(numericId) && numericId > 0
  })

  const childTeams = useMemo(
    () => (childTeamsPage?.results ?? []).filter((t) => t.parent === numericId),
    [childTeamsPage, numericId]
  )

  // Divisions for parent select
  const { data: divisionsPage } = useQuery({
    ...organizationsTeamsListOptions({
      query: { organization: workspaceId, type: ['branch'], per_page: 200 }
    }),
    enabled: idValid
  })

  const divisionOptions = useMemo(
    () =>
      (divisionsPage?.results ?? []).map((d) => ({
        label: d.name,
        value: d.id
      })),
    [divisionsPage]
  )

  // Org members for owner select
  const { data: accessPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: idValid
  })

  const memberOptions = useMemo(
    () =>
      (accessPage?.results ?? []).map((a) => ({
        label:
          `${a.user.first_name} ${a.user.last_name}`.trim() || a.user.email,
        value: a.user.id
      })),
    [accessPage]
  )

  // Parent division label
  const parentDivision = useMemo(() => {
    if (!department?.parent || !divisionsPage?.results) return null
    return divisionsPage.results.find((d) => d.id === department.parent)
  }, [department, divisionsPage])

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: organizationsTeamsRetrieveQueryKey({
          path: { id: numericId }
        })
      }),
      queryClient.invalidateQueries({
        queryKey: organizationsTeamsListQueryKey()
      })
    ])
  }

  const updateMutation = useMutation({
    ...organizationsTeamsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.departmentUpdated'))
      setEditOpen(false)
      await invalidateAll()
    },
    onError: (e) => showApiErrors(e)
  })

  const createTeamMutation = useMutation({
    ...organizationsTeamsCreateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.teamAdded'))
      setAddTeamOpen(false)
      teamForm.resetFields()
      await invalidateAll()
    },
    onError: (e) => showApiErrors(e)
  })

  const startEditing = () => {
    if (!department) return
    editForm.setFieldsValue({
      name: department.name,
      abbreviation: department.abbreviation ?? undefined,
      description: department.description ?? undefined,
      head_user: department.head_user ?? undefined,
      parent: department.parent ?? undefined
    })
    setEditOpen(true)
  }

  const ownerLabel = useMemo(() => {
    if (department?.head_user_detail) {
      const d = department.head_user_detail

      return `${d.first_name} ${d.last_name}`.trim() || d.email
    }
    if (department?.top_users?.length) {
      return department.top_users
        .map((u) => `${u.first_name} ${u.last_name}`.trim() || u.email)
        .join(', ')
    }
    return '—'
  }, [department])

  const columns: ColumnsType<TeamList> = useMemo(
    () => [
      {
        fixed: 'left',
        title: t('workspace.orgChart.columns.name'),
        dataIndex: 'name'
      },
      {
        title: t('workspace.orgChart.columns.members'),
        dataIndex: 'users_count'
      },
      {
        title: <TableActionColumnTitle />,
        key: 'actions',
        width: 120,
        render: (_, row) => (
          <Button
            type="link"
            onClick={() =>
              navigate(
                paths.workspaces.membersTeam(workspaceRouteId, String(row.id))
              )
            }
          >
            {t('workspace.orgChart.actions.manage')}
          </Button>
        )
      }
    ],
    [t, navigate, workspaceRouteId]
  )

  const backButton = (
    <Button
      type="link"
      icon={<ChevronLeft size={16} />}
      onClick={() => navigate(paths.workspaces.members(workspaceRouteId))}
      style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
    >
      {t('workspace.orgChart.backToMembers')}
    </Button>
  )

  if (isLoading) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <Card size="small" variant="outlined">
          <Skeleton active />
        </Card>
      </Flex>
    )
  }

  if (isError) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <QueryErrorAlert onRetry={() => void refetch()} />
      </Flex>
    )
  }

  if (!department) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <Typography.Title level={5}>
          {t('workspace.orgChart.notFound')}
        </Typography.Title>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      {backButton}

      <Card
        size="small"
        variant="outlined"
        title={department.name}
        styles={orgEntityListCardHeaderStyles}
        extra={
          <Button
            type="text"
            icon={<Pencil size={14} />}
            onClick={startEditing}
          >
            {t('workspace.orgChart.actions.edit')}
          </Button>
        }
      >
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item
            label={t('workspace.orgChart.fields.abbreviation')}
          >
            {department.abbreviation || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.fields.description')}>
            {department.description || '—'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('workspace.orgChart.fields.parentDivision')}
          >
            {parentDivision ? (
              <Button
                type="link"
                style={{ padding: 0 }}
                onClick={() =>
                  navigate(
                    paths.workspaces.membersDivision(
                      workspaceRouteId,
                      String(department.parent)
                    )
                  )
                }
              >
                {parentDivision.name}
              </Button>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.fields.owner')}>
            {ownerLabel}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.columns.members')}>
            {department.users_count}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.columns.teams')}>
            {childTeams.length}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <ProTableContainer<TeamList>
        showSearch={false}
        rowKey="id"
        columns={columns}
        dataSource={childTeams}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        leftChildren={
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {t('workspace.orgChart.department.teamsTableTitle')}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgChart.department.teamsTableHint')}
            </Typography.Text>
          </Flex>
        }
        rightChildren={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              teamForm.resetFields()
              setAddTeamOpen(true)
            }}
          >
            {t('workspace.orgChart.teams.add')}
          </Button>
        }
      />

      {/* Edit drawer */}
      <Drawer
        title={t('workspace.orgChart.departments.editDrawerTitle')}
        size={480}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={updateMutation.isPending}
              onClick={() => editForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            updateMutation.mutate({
              path: { id: numericId },
              body: {
                name: values.name,
                abbreviation: values.abbreviation ?? null,
                description: values.description,
                head_user: values.head_user ?? null,
                parent: values.parent ?? null
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields
            t={t}
            memberOptions={memberOptions}
            parentLabel={t('workspace.orgChart.fields.parentDivision')}
            parentOptions={divisionOptions}
          />
        </Form>
      </Drawer>

      {/* Add team drawer */}
      <Drawer
        title={t('workspace.orgChart.department.addTeamDrawerTitle')}
        size={480}
        open={addTeamOpen}
        onClose={() => setAddTeamOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setAddTeamOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={createTeamMutation.isPending}
              onClick={() => teamForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={teamForm}
          layout="vertical"
          onFinish={(values) => {
            createTeamMutation.mutate({
              body: {
                name: values.name,
                type: 'section',
                parent: numericId,
                organization: workspaceId
              }
            })
          }}
        >
          <Form.Item
            name="name"
            label={t('workspace.orgChart.fields.name')}
            rules={[{ required: true, message: t('required_field_error') }]}
          >
            <Input placeholder={t('placeholders.name')} />
          </Form.Item>
        </Form>
      </Drawer>
    </Flex>
  )
}
